// Email validation with typo detection + auto-suggest for common domain misspellings.
// Used at the public test gate to prevent students from entering wrong emails
// like @gamil.com, @gmail.con, @yahoooo.com, @hotmial.com etc.

// Whitelist of legitimate domains we want to recognize.
const KNOWN_DOMAINS = [
  'gmail.com',
  'yahoo.com',
  'hotmail.com',
  'outlook.com',
  'icloud.com',
  'live.com',
  'aol.com',
  'protonmail.com',
  'proton.me',
  'rediffmail.com',
  'zoho.com',
  'yandex.com',
  'mail.com',
  'gmx.com',
  'fastmail.com',
  'radianceedu.com',
  'radianceedu.net',
];

// Explicit typo → correction map (covers ~95% of real mistakes).
const TYPO_MAP = {
  // Gmail
  'gamil.com': 'gmail.com',
  'gnail.com': 'gmail.com',
  'gmial.com': 'gmail.com',
  'gmal.com': 'gmail.com',
  'gmaill.com': 'gmail.com',
  'gmail.con': 'gmail.com',
  'gmail.co': 'gmail.com',
  'gmail.cm': 'gmail.com',
  'gmail.cmo': 'gmail.com',
  'gmail.om': 'gmail.com',
  'gmaim.com': 'gmail.com',
  'gemail.com': 'gmail.com',
  'gmail.comm': 'gmail.com',
  'gmail.coom': 'gmail.com',
  'gmali.com': 'gmail.com',
  'gail.com': 'gmail.com',
  // Yahoo
  'yaho.com': 'yahoo.com',
  'yahooo.com': 'yahoo.com',
  'yahoo.con': 'yahoo.com',
  'yahoo.co': 'yahoo.com',
  'yahoo.cm': 'yahoo.com',
  'yahho.com': 'yahoo.com',
  'yhoo.com': 'yahoo.com',
  'yahoom.com': 'yahoo.com',
  // Hotmail
  'hotmial.com': 'hotmail.com',
  'hotnail.com': 'hotmail.com',
  'hotmail.con': 'hotmail.com',
  'hotmail.co': 'hotmail.com',
  'hotmaill.com': 'hotmail.com',
  'hormail.com': 'hotmail.com',
  // Outlook
  'outloook.com': 'outlook.com',
  'outlook.con': 'outlook.com',
  'outlock.com': 'outlook.com',
  'outluk.com': 'outlook.com',
  // iCloud
  'icoud.com': 'icloud.com',
  'iclod.com': 'icloud.com',
  'icloud.con': 'icloud.com',
};

// Levenshtein distance between two strings (small inputs only).
function levenshtein(a, b) {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  const matrix = [];
  for (let i = 0; i <= b.length; i++) matrix[i] = [i];
  for (let j = 0; j <= a.length; j++) matrix[0][j] = j;
  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }
  return matrix[b.length][a.length];
}

// Find closest known domain via Levenshtein distance (catches unknown typos).
function findFuzzyMatch(domain) {
  if (!domain) return null;
  let best = null;
  let bestDist = Infinity;
  for (const known of KNOWN_DOMAINS) {
    const d = levenshtein(domain, known);
    if (d < bestDist) {
      bestDist = d;
      best = known;
    }
  }
  // Only suggest if it's clearly a typo (distance 1-2) AND not already valid
  if (bestDist >= 1 && bestDist <= 2 && best !== domain) {
    return best;
  }
  return null;
}

/**
 * Validates an email and returns { ok, suggestion, reason }.
 *  - ok=true, suggestion=null  → email looks valid, allow.
 *  - ok=false, suggestion=str  → typo detected, blocking. Show suggestion to user.
 *  - ok=false, suggestion=null → invalid format, blocking.
 */
export function validateEmail(rawEmail) {
  const email = (rawEmail || '').trim().toLowerCase();

  if (!email) {
    return { ok: false, suggestion: null, reason: 'Please enter your email' };
  }

  // Basic shape: must have local@domain.tld
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    return { ok: false, suggestion: null, reason: 'Please enter a valid email address' };
  }

  const atIdx = email.lastIndexOf('@');
  const local = email.slice(0, atIdx);
  const domain = email.slice(atIdx + 1);

  // Empty local or short domain
  if (!local || domain.length < 4) {
    return { ok: false, suggestion: null, reason: 'Please enter a valid email address' };
  }

  // 1. Explicit typo lookup (highest confidence)
  if (TYPO_MAP[domain]) {
    return {
      ok: false,
      suggestion: `${local}@${TYPO_MAP[domain]}`,
      reason: `Did you mean ${local}@${TYPO_MAP[domain]}?`,
    };
  }

  // 2. Domain is exactly a known good domain → accept
  if (KNOWN_DOMAINS.includes(domain)) {
    return { ok: true, suggestion: null, reason: null };
  }

  // 3. Fuzzy match against known domains for unknown typos
  const fuzzy = findFuzzyMatch(domain);
  if (fuzzy) {
    return {
      ok: false,
      suggestion: `${local}@${fuzzy}`,
      reason: `Did you mean ${local}@${fuzzy}?`,
    };
  }

  // 4. Unknown but well-formed domain (custom corporate / educational) → accept.
  return { ok: true, suggestion: null, reason: null };
}
